<template>
    <CRow>
        <CCol col="12">
            <CCard>
                <CCardHeader class="d-flex align-items-center justify-content-between flex-wrap">
                    <strong>Контент партнёров</strong>
                    <CButton color="primary" size="sm" :disabled="running" @click="runCheck">
                        {{ running ? 'Проверка…' : 'Проверить сейчас' }}
                    </CButton>
                </CCardHeader>
                <CCardBody>
                    <CRow class="mb-3">
                        <CCol cols="12" md="3" class="mb-2">
                            <div class="text-muted">Последняя проверка</div>
                            <div>{{ summary.checkedAt ? formatDate(summary.checkedAt) : 'ещё не было' }}</div>
                        </CCol>
                        <CCol cols="6" md="2" class="mb-2">
                            <div class="text-muted">Ок</div>
                            <div class="text-success h5 mb-0">{{ summary.ok }}</div>
                        </CCol>
                        <CCol cols="6" md="2" class="mb-2">
                            <div class="text-muted">Не ок</div>
                            <div class="text-danger h5 mb-0">{{ summary.bad }}</div>
                        </CCol>
                    </CRow>
                    <CRow class="mb-3">
                        <CCol cols="12" md="3" class="mb-2">
                            <CSelect
                                :value.sync="status"
                                :options="statusOptions"
                            />
                        </CCol>
                        <CCol cols="12" md="3" class="mb-2">
                            <CSelect
                                :value.sync="type"
                                :options="typeOptions"
                            />
                        </CCol>
                        <CCol cols="12" md="4" class="mb-2">
                            <CInput
                                v-model="search"
                                placeholder="Партнёр или карточка"
                                @keyup.enter="applyFilters"
                            />
                        </CCol>
                        <CCol cols="12" md="2" class="mb-2">
                            <CButton color="secondary" class="w-100" @click="applyFilters">Найти</CButton>
                        </CCol>
                    </CRow>

                    <div v-if="loading" class="py-4 text-center">
                        <CSpinner color="primary" />
                    </div>
                    <template v-else>
                        <div v-if="!items.length" class="text-muted">
                            Проверок ещё не было или по фильтру ничего нет.
                        </div>
                        <CDataTable
                            v-else
                            hover
                            :items="items"
                            :fields="fields"
                        >
                            <template #partnerTitle="{ item }">
                                <td>
                                    <CLink v-if="item.partnerPath" :to="item.partnerPath">{{ item.partnerTitle || '—' }}</CLink>
                                    <span v-else>—</span>
                                </td>
                            </template>
                            <template #venueTitle="{ item }">
                                <td>
                                    <CLink :to="detailLink(item)">{{ item.venueTitle }}</CLink>
                                </td>
                            </template>
                            <template #ok="{ item }">
                                <td :class="item.ok ? 'text-success' : 'text-danger'">
                                    {{ item.ok ? 'ок' : 'не ок' }}
                                </td>
                            </template>
                            <template #reasons="{ item }">
                                <td>{{ reasonText(item) }}</td>
                            </template>
                            <template #uptime="{ item }">
                                <td>{{ formatUptime(item.uptime && item.uptime.h24) }} / {{ formatUptime(item.uptime && item.uptime.d7) }} / {{ formatUptime(item.uptime && item.uptime.d30) }}</td>
                            </template>
                            <template #checkedAt="{ item }">
                                <td>{{ formatDate(item.checkedAt) }}</td>
                            </template>
                        </CDataTable>
                        <CPagination
                            v-if="totalPages > 1"
                            :active-page.sync="page"
                            class="mt-3"
                            :pages="totalPages"
                            align="center"
                        />
                    </template>
                </CCardBody>
            </CCard>
        </CCol>
    </CRow>
</template>

<script>
    import Vue from 'vue';
    import moment from 'moment';

    export default {
        name: 'ContentHealth',

        data () {
            return {
                loading: false,
                running: false,
                items: [],
                total: 0,
                page: 1,
                pageSize: 20,
                status: 'all',
                type: '',
                search: '',
                summary: { ok: 0, bad: 0, checkedAt: null },
                statusOptions: [
                    { value: 'all', label: 'Все статусы' },
                    { value: 'bad', label: 'Не ок' },
                    { value: 'ok', label: 'Ок' }
                ],
                typeOptions: [
                    { value: '', label: 'Все типы' },
                    { value: 'restaurant', label: 'Ресторан' },
                    { value: 'grocer', label: 'Магазин' },
                    { value: 'florist', label: 'Цветы' },
                    { value: 'pharmacy', label: 'Аптека' },
                    { value: 'entity', label: 'Услуга' },
                    { value: 'laundry', label: 'Прачечная' },
                    { value: 'assistant', label: 'Помощник' }
                ],
                fields: [
                    { key: 'partnerTitle', label: 'Партнёр' },
                    { key: 'venueTitle', label: 'Карточка' },
                    { key: 'venueTypeTitle', label: 'Тип' },
                    { key: 'ok', label: 'Статус' },
                    { key: 'reasons', label: 'Причины' },
                    { key: 'uptime', label: 'Аптайм 24ч / 7д / 30д' },
                    { key: 'checkedAt', label: 'Проверено' }
                ]
            };
        },

        computed: {
            totalPages () {
                return Math.max(1, Math.ceil(this.total / this.pageSize));
            }
        },

        watch: {
            page () {
                this.loadData();
            },
            status () {
                this.resetAndLoad();
            },
            type () {
                this.resetAndLoad();
            }
        },

        mounted () {
            this.loadData();
        },

        methods: {
            formatDate (date) {
                return date ? moment(date).format('DD.MM.YYYY HH:mm') : '—';
            },
            formatUptime (value) {
                return value == null ? '—' : value + '%';
            },
            reasonText (item) {
                const reasons = item.reasons || [];
                if (!reasons.length) {
                    return item.ok ? '' : '—';
                }
                return reasons.map(reason => reason.message).join('; ');
            },
            detailLink (item) {
                return '/content-health/' + item.venueType + '/' + item.venueId;
            },
            resetAndLoad () {
                if (this.page !== 1) {
                    this.page = 1;
                    return;
                }
                this.loadData();
            },
            applyFilters () {
                this.resetAndLoad();
            },
            loadData () {
                this.loading = true;
                Vue.axios
                    .get('/content-health', {
                        params: {
                            page: this.page,
                            pageSize: this.pageSize,
                            status: this.status,
                            type: this.type,
                            q: this.search
                        }
                    })
                    .then(resp => {
                        this.items = resp.data.results || [];
                        this.total = resp.data.total || 0;
                        this.summary = resp.data.summary || this.summary;
                    })
                    .catch(err => {
                        this.$root.$emit('showError', 'Ошибка загрузки проверки контента. ' + err);
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            },
            runCheck () {
                this.running = true;
                Vue.axios
                    .post('/content-health/run')
                    .then(() => {
                        if (this.page !== 1) {
                            this.page = 1;
                            return;
                        }
                        this.loadData();
                    })
                    .catch(err => {
                        const message = err.response && err.response.data && err.response.data.error;
                        this.$root.$emit('showError', message || ('Ошибка проверки. ' + err));
                    })
                    .finally(() => {
                        this.running = false;
                    });
            }
        }
    };
</script>
